import { z } from 'zod';
import { CPF_ERROR_COPY } from '../copy';
import {
  CEREMONY_MODE_VALUES,
  DEADLINE_PRESET_VALUES,
  DOCUMENT_FORMAT_VALUES,
  ENVELOPE_STATUS_VALUES,
  PARTICIPANT_ROLE_VALUES,
  PARTICIPANT_STATUS_VALUES,
  SIGNATURE_LEVEL_VALUES,
  SIGNATURE_MODALITY_VALUES,
  VERIFICATION_CHECK_STATUS_VALUES,
  VERIFICATION_VERDICT_VALUES,
  type OwnerWebEnvelopeView,
} from './types';
import { STAMP_PRESET_VALUES } from './visual-stamp';

/**
 * Dois blocos de Zod, com propósitos diferentes:
 *
 *  1. **saída** (`createEnvelopePayloadSchema`, `startEnvelopeRequestSchema`) — o
 *     que o wizard pode enviar. Defesa em profundidade + erro cedo (o BFF valida
 *     de novo: soberania do servidor, IMVP-14). Camelo aqui, snake_case só depois
 *     do adapter — nenhuma página monta JSON de wire na mão.
 *  2. **entrada** (`ownerWeb*Schema`, `verificationReportDtoSchema`) — validação
 *     em RUNTIME da resposta. `Record<Enum, …>` protege em compilação; o dado que
 *     chega pela rede não passa por compilador nenhum. Status fora do enum real
 *     falha aqui e vira `ContractViolationError` no adapter, nunca um rótulo
 *     plausível porém falso (mandato §9).
 *
 * **Assimetria deliberada em modalidade:** a saída nunca aceita `avancada`
 * (fora do MVP, mandato §1) — a entrada aceita, porque um envelope criado por
 * `/v1` com nível avançado chega na lista do owner e recusá-lo derrubaria a
 * tela por um valor legítimo do motor. Nunca aceita alias, papel desconhecido
 * ou stage fora do desenho.
 */

export const SIGNATURE_MODALITIES = SIGNATURE_MODALITY_VALUES;
export const DEADLINE_PRESETS = DEADLINE_PRESET_VALUES;

const emailSchema = z.string().trim().email('e-mail inválido').max(320);
const nameSchema = z.string().trim().min(1, 'nome obrigatório').max(200);

// =========================================================================
// CPF (mandato §10 — decisão do Felipe): qualificada obrigatório · simples
// opcional · observer não tem o campo.
// =========================================================================

/** Dígitos verificadores do CPF. Erro cedo é UX; o backend valida de novo. */
export function isValidCpf(digits: string): boolean {
  if (!/^\d{11}$/.test(digits)) return false;
  // Sequências de dígito repetido passam no cálculo do DV mas não são CPF.
  if (/^(\d)\1{10}$/.test(digits)) return false;

  const checkDigit = (length: number): number => {
    let sum = 0;
    for (let index = 0; index < length; index += 1) {
      sum += Number(digits[index]) * (length + 1 - index);
    }
    const rest = (sum * 10) % 11;
    return rest === 10 ? 0 : rest;
  };

  return checkDigit(9) === Number(digits[9]) && checkDigit(10) === Number(digits[10]);
}

/** Normaliza para 11 dígitos crus (aceita `123.456.789-09` digitado pelo owner)
 * e trata vazio como ausência declarada (`null`), nunca como `''`. */
function normalizeCpfInput(value: unknown): unknown {
  if (value === null || value === undefined) return null;
  if (typeof value !== 'string') return value;
  const digits = value.replace(/\D/g, '');
  return digits.length === 0 ? null : digits;
}

export const cpfFieldSchema = z.preprocess(
  normalizeCpfInput,
  z.union([
    z.null(),
    z
      .string()
      .regex(/^\d{11}$/, CPF_ERROR_COPY.format)
      .refine(isValidCpf, CPF_ERROR_COPY.checkDigit),
  ]),
);

// =========================================================================
// Saída — payload do wizard
// =========================================================================

/**
 * Carimbo visível (D-027, opt-in) **na linguagem do formulário**: preset + página. As frações
 * que o contrato transporta nascem no adapter (`lib/api/visual-stamp.ts`) — o wizard nunca monta
 * geometria à mão. `null` = sem carimbo, e é o default de todo signatário.
 */
const stampDraftSchema = z
  .object({
    preset: z.enum(STAMP_PRESET_VALUES, {
      errorMap: () => ({ message: 'escolha uma posição de carimbo válida' }),
    }),
    /** Páginas são 1-based no contrato (`zVisualPlacement.page`), como no leitor de PDF. */
    page: z.number().int().min(1, 'a página do carimbo começa em 1'),
  })
  .strict();

const signerParticipantSchema = z
  .object({
    role: z.literal('signer'),
    name: nameSchema,
    email: emailSchema,
    modality: z.enum(SIGNATURE_MODALITIES, {
      errorMap: () => ({ message: 'escolha Simples ou Qualificada' }),
    }),
    stage: z.number().int().min(1),
    /** `null` = não informado. Obrigatório só em `qualificada` (regra no
     * `superRefine` do payload, onde o caminho do erro aponta o participante). */
    cpf: cpfFieldSchema,
    /** `.default(null)` para o campo EXISTIR sempre no form (o wizard antigo não o preenche, e
     * o round-trip do adapter compara forma por forma). Ausência e `null` são o mesmo caso. */
    stamp: stampDraftSchema.nullable().default(null),
  })
  .strict();

/** Observador não assina e por isso **não tem** os campos `cpf` nem `stamp` — `.strict()`
 * rejeita se vierem (a mesma regra do contrato do backend: quem não assina não tem aparência de
 * assinatura para carimbar). O `null` explícito que o backend exige em `signature_level`
 * e `stage` é emitido pelo adapter, não digitado no formulário. */
const observerParticipantSchema = z
  .object({
    role: z.literal('observer'),
    name: nameSchema,
    email: emailSchema,
  })
  .strict();

export const participantSchema = z.discriminatedUnion('role', [
  signerParticipantSchema,
  observerParticipantSchema,
]);

export type SignerParticipantInput = z.infer<typeof signerParticipantSchema>;
export type ObserverParticipantInput = z.infer<typeof observerParticipantSchema>;
export type ParticipantInput = z.infer<typeof participantSchema>;

/**
 * Etapas dos signers: inteiros contíguos a partir de 1, sem buraco; dentro da
 * etapa a ordem é livre (não há campo de ordem — só o número da etapa).
 * "Qualquer ordem" (mandato §6) **é** o caso `stage: 1` para todo signer — por
 * isso não existe mais um campo `stageMode` no payload: o backend deriva o
 * `workflow` dos stages e `.strict()` rejeitaria o campo extra.
 * Observador nunca entra nessa contagem (fora de etapas, mandato §6).
 */
function validateStageShape(participants: ParticipantInput[], ctx: z.RefinementCtx) {
  const signers = participants.filter(
    (p): p is SignerParticipantInput => p.role === 'signer',
  );
  if (signers.length === 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'é preciso ao menos um signatário',
      path: ['participants'],
    });
    return;
  }

  const usedStages = Array.from(new Set(signers.map((s) => s.stage))).sort((a, b) => a - b);
  const contiguous = usedStages.every((stage, index) => stage === index + 1);
  if (!contiguous) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'etapas precisam ser contíguas, começando em 1, sem pular número',
      path: ['participants'],
    });
  }
}

/** Qualificada exige CPF; Simples aceita ausência. O erro aponta o participante
 * exato para a UI conseguir destacar o campo certo. */
function validateCpfRule(participants: ParticipantInput[], ctx: z.RefinementCtx) {
  participants.forEach((participant, index) => {
    if (participant.role !== 'signer') return;
    if (participant.modality === 'qualificada' && participant.cpf === null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: CPF_ERROR_COPY.required,
        path: ['participants', index, 'cpf'],
      });
    }
    // Espelho da regra do contrato: o carimbo desenha o CPF mascarado, então sem CPF não há
    // carimbo possível — o backend recusa a criação e a assinatura falharia na selagem. Só
    // alcança a Simples (na Qualificada o CPF já é obrigatório acima).
    if (participant.stamp !== null && participant.cpf === null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: CPF_ERROR_COPY.requiredForStamp,
        path: ['participants', index, 'cpf'],
      });
    }
  });
}

export const createEnvelopePayloadSchema = z
  .object({
    deadlinePreset: z.enum(DEADLINE_PRESETS),
    /** Instante ISO **com offset** já resolvido do preset (ou do custom revisado)
     * — sempre explícito, nunca recalculado às cegas no servidor sem o que o
     * owner viu na revisão (mandato §7). */
    deadlineAt: z.string().datetime({ offset: true, message: 'prazo inválido' }),
    /** Fuso IANA do navegador do owner — obrigatório no backend. */
    timezone: z.string().trim().min(1, 'fuso horário obrigatório').max(64),
    participants: z.array(participantSchema).min(1).max(100),
  })
  .strict()
  .superRefine((payload, ctx) => {
    validateStageShape(payload.participants, ctx);
    validateCpfRule(payload.participants, ctx);

    const emails = payload.participants.map((p) => p.email.toLowerCase());
    const duplicated = emails.some((email, index) => emails.indexOf(email) !== index);
    if (duplicated) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'e-mails de participantes não podem se repetir',
        path: ['participants'],
      });
    }
  });

export type CreateEnvelopePayload = z.infer<typeof createEnvelopePayloadSchema>;

export const startEnvelopeRequestSchema = z
  .object({
    confirmation: z.literal(true),
    expectedUpdatedAt: z.string().datetime({ offset: true }),
  })
  .strict();

export type StartEnvelopeRequest = z.infer<typeof startEnvelopeRequestSchema>;

export const otpRequestSchema = z.object({ email: emailSchema }).strict();
export const otpVerifySchema = z
  .object({
    verification_handle: z.string().min(1),
    code: z.string().regex(/^\d{6}$/, 'código de 6 dígitos'),
  })
  .strict();

// =========================================================================
// Entrada — validação em runtime da resposta do BFF (snake_case, wire)
//
// Sem `.strict()`: campo NOVO no backend não pode derrubar a tela do owner
// (zod descarta o que não conhece). O que precisa ser fechado é o vocabulário
// de status — e esse é enum aqui.
// =========================================================================

const isoString = z.string().min(1);

/**
 * Posicionamento do carimbo na LEITURA. Só a forma é conferida (números e página 1-based), sem
 * repetir os refinamentos de borda do backend: o que chega aqui já foi validado por
 * `zVisualPlacement` na criação, e derrubar a tela de acompanhamento por causa de um carimbo
 * fora de margem seria trocar um problema cosmético por uma tela inteira quebrada.
 */
const visualPlacementViewSchema = z.object({
  page: z.number().int().min(1),
  x: z.number(),
  y: z.number(),
  width: z.number(),
  height: z.number(),
});

export const ownerWebParticipantViewSchema = z.object({
  id: z.string().min(1),
  name: z.string(),
  email: z.string().nullable(),
  role: z.enum(PARTICIPANT_ROLE_VALUES),
  stage: z.number().int().nullable(),
  // LEITURA aceita os 3 níveis do enum real: `avancada` chega em envelope criado
  // por `/v1` e recusá-la aqui derrubaria a lista inteira do owner por um valor
  // legítimo do motor. A CRIAÇÃO segue restrita a Simples/Qualificada.
  required_signature_level: z.enum(SIGNATURE_LEVEL_VALUES).nullable(),
  actual_signature_level: z.enum(SIGNATURE_LEVEL_VALUES).nullable(),
  status: z.enum(PARTICIPANT_STATUS_VALUES),
  created_at: isoString,
  updated_at: isoString,
  channel_verified_at: z.string().nullable(),
  signed_at: z.string().nullable(),
  decline_reason: z.string().nullable(),
  /** `null` = sem carimbo. A chave é sempre emitida pelo backend — ausência dela é contrato
   * violado, não "sem carimbo" (a tela precisa poder AFIRMAR que não há). */
  visual: visualPlacementViewSchema.nullable(),
});

/**
 * Documento do detalhe (R-UX02-P02/L3B-B) — MIRROR LOCAL do shape novo, no mesmo
 * espírito de `ownerWebEnvelopeListItemSchema` abaixo: não importa de
 * `@argink/shared/owner-web` porque a lane que edita o backend/shared roda em
 * paralelo a esta. `.nullable()` no ponto de uso (`ownerWebEnvelopeViewSchema`):
 * envio sem documento associado é estado legítimo, distinto de "documento sem
 * nome".
 */
const ownerWebEnvelopeDetailDocumentSchema = z.object({
  id: z.string().min(1),
  name: z.string().nullable(),
  format: z.enum(DOCUMENT_FORMAT_VALUES),
  hash_sha256: z.string(),
  page_count: z.number().int().positive().nullable(),
  created_at: isoString,
  signed: z.boolean(),
});

/**
 * `workflow` (`any_order`/`staged`, lossy) DEIXA DE EXISTIR: o backend expõe
 * `ceremony_mode` (os 4 valores reais do enum `CeremonyMode` do Prisma) e
 * `quorum` direto. Sem `.strict()`, como o desenho original — campo NOVO no
 * backend não pode derrubar o detalhe (diferente da LISTA, endurecida de
 * propósito logo abaixo).
 *
 * A anotação `z.ZodType<OwnerWebEnvelopeView>` (B4/Correção B) prende este
 * schema ao MESMO tipo que `types.ts` deriva do DTO compartilhado
 * (`@argink/shared/owner-web`): campo que sair de um lado sem sair do outro para
 * de compilar aqui, sem exigir `.strict()` em runtime.
 */
export const ownerWebEnvelopeViewSchema: z.ZodType<OwnerWebEnvelopeView> = z.object({
  id: z.string().min(1),
  status: z.enum(ENVELOPE_STATUS_VALUES),
  ceremony_mode: z.enum(CEREMONY_MODE_VALUES),
  quorum: z.number().int().positive().nullable(),
  current_stage: z.number().int().nullable(),
  created_at: isoString,
  updated_at: isoString,
  expires_at: z.string().nullable(),
  document: ownerWebEnvelopeDetailDocumentSchema.nullable(),
  participants: z.array(ownerWebParticipantViewSchema),
});

export const ownerWebEnvelopeSummarySchema = z.object({
  id: z.string().min(1),
  status: z.enum(ENVELOPE_STATUS_VALUES),
  document_name: z.string().nullable(),
  created_at: isoString,
  updated_at: isoString,
  expires_at: z.string().nullable(),
  signer_count: z.number().int(),
  observer_count: z.number().int(),
  signed_count: z.number().int(),
});

/**
 * Espelho ZOD de `OwnerWebEnvelopeListItem` (R-UX02-P02/L3A-2b) — item de
 * `GET /web/envelopes`, **`.strict()`**: campo ausente ou extra falha o parse, não só
 * o `tsc`. Ao contrário de `ownerWebEnvelopeSummarySchema` (Dashboard, lenient de
 * propósito), a LISTA é o contrato endurecido desta rodada — um campo novo do
 * backend que este cliente não conhece também é recusado (fail-closed, mandato §9),
 * nunca ignorado em silêncio.
 *
 * A combinação `document_id === null && document_name !== null` é IMPOSSÍVEL (os dois
 * vêm do MESMO documento resolvido no backend) e é recusada aqui em defesa de
 * profundidade, espelhando `zOwnerWebEnvelopeListItem` (`@argink/shared/owner-web`).
 */
export const ownerWebEnvelopeListItemSchema = z
  .object({
    id: z.string().min(1),
    status: z.enum(ENVELOPE_STATUS_VALUES),
    document_name: z.string().nullable(),
    created_at: isoString,
    updated_at: isoString,
    expires_at: z.string().nullable(),
    signer_count: z.number().int(),
    observer_count: z.number().int(),
    signed_count: z.number().int(),
    /** `null` = envio sem documento associado nenhum. Presente + `document_name: null`
     * é o caso comum ("Documento sem nome"). */
    document_id: z.string().min(1).nullable(),
    /** `max(Envelope.updatedAt, Signer.updatedAt)` — nunca conclusão/recusa/cancelamento. */
    last_activity_at: isoString,
  })
  .strict()
  .superRefine((item, ctx) => {
    if (item.document_id === null && item.document_name !== null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['document_id'],
        message: 'document_id nulo com document_name preenchido é uma combinação impossível',
      });
    }
  });

/**
 * Espelho ZOD de `OwnerWebEnvelopeList` (R-UX02-P02/L3A-2b) — `.strict()` na resposta
 * inteira e as três invariantes numéricas do mandato: `total_returned` tem que bater
 * com `items.length`, nunca pode passar de `total_filtered`, que por sua vez nunca
 * pode passar de `total`. Uma violação de qualquer uma vira `ContractViolationError`
 * no adapter — nunca reinterpretada como lista vazia.
 */
export const ownerWebEnvelopeListSchema = z
  .object({
    items: z.array(ownerWebEnvelopeListItemSchema),
    next_cursor: z.string().nullable(),
    total: z.number().int().min(0),
    total_filtered: z.number().int().min(0),
    total_returned: z.number().int().min(0),
  })
  .strict()
  .superRefine((list, ctx) => {
    if (list.total_returned !== list.items.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['total_returned'],
        message: 'total_returned tem que ser exatamente items.length',
      });
    }
    if (list.total_returned > list.total_filtered) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['total_returned'],
        message: 'total_returned não pode exceder total_filtered',
      });
    }
    if (list.total_filtered > list.total) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['total_filtered'],
        message: 'total_filtered não pode exceder total',
      });
    }
  });

export const verificationCheckDtoSchema = z.object({
  name: z.string(),
  status: z.enum(VERIFICATION_CHECK_STATUS_VALUES),
  reasons: z.array(z.string()),
  detail: z.record(z.unknown()),
  notes: z.array(z.string()),
});

export const verificationSummaryDtoSchema = z.object({
  passed: z.number().int(),
  failed: z.number().int(),
  indeterminate: z.number().int(),
  skipped: z.number().int(),
  failedChecks: z.array(z.string()),
  indeterminateChecks: z.array(z.string()),
  reasons: z.array(z.string()),
});

/** Devolvido **puro** pela rota — inclusive sem `checkedAt`, que nunca existiu. */
export const verificationReportDtoSchema = z.object({
  verdict: z.enum(VERIFICATION_VERDICT_VALUES),
  verifierVersion: z.string(),
  bundleFormatId: z.string(),
  manifestHash: z.string().nullable(),
  manifestSchemaVersion: z.number().int().nullable(),
  sealKeyId: z.string().nullable(),
  manifest: z.unknown(),
  checks: z.array(verificationCheckDtoSchema),
  summary: verificationSummaryDtoSchema,
  notes: z.array(z.string()),
});

export const sessionInfoDtoSchema = z.object({
  user: z.object({ id: z.string(), name: z.string(), email: z.string() }),
  membership: z.object({ id: z.string(), role: z.literal('owner') }),
  tenant: z.object({ id: z.string() }),
  expires_at: isoString,
  environment: z.enum(['development', 'production']).optional(),
  capabilities: z.object({ evidence_delivery: z.boolean() }),
});

/** Resposta de POST /web/envelopes/:id/validate (D-063/#100) — shape exata do BFF
 * (`OwnerWebValidateResult` em @argink/shared/owner-web); `.strict()` como as demais. */
export const signedDocumentValidationDtoSchema = z
  .object({
    result: z.enum(['valid', 'invalid', 'indeterminate']),
    indication: z.string().min(1),
    sub_indication: z.string().nullable(),
    signature_count: z.number().int().nonnegative(),
    checked_at: z.string().min(1),
  })
  .strict();
